import { z } from 'zod';
import type { Widget } from './Widget';

export enum WidgetState {
    CREATED,
    STOPPED,
    PLAYING,
    PAUSED,
}

export enum WidgetType {
    AD = 'ad',
    ANNOUNCEMENT = 'announcement',
    BROADSIGN = 'broadsign',
    CONTENT = 'content',
    CUSTOM_HTML = 'customhtml',
    GENERAL = 'general',
    GRAPHIC = 'graphic',
    HIVESTACK = 'hivestack',
    IFRAME = 'iframe',
    IFRAME_PREMIUM = 'iframepremium',
    LABEL = 'label',
    LIST = 'list',
    MAPS = 'maps',
    MEDALLION = 'medallion',
    PLACE_EXCHANGE = 'place_exchange',
    POWERBI = 'powerbi',
    SCHEDULE = 'schedule',
    SCORES = 'scores',
    TICKER = 'ticker',
    TIME_DATE = 'timedate',
    VENGO = 'vengo',
    VIDEO_STREAM = 'video_stream',
    VISTAR = 'vistar',
    WEATHER = 'weather',
    WEATHER_RADAR = 'weather_radar',
    COUNTER = 'counter',
    RADAR = 'radar',
    QUOTE = 'quote',
}

const ZodWidgetType = z.nativeEnum(WidgetType);

export const ZodGenericWidgetConfigurationDetails = z
    .object({
        width: z.nullable(z.string()),
        height: z.nullable(z.string()),
        x: z.nullable(z.string()),
        y: z.nullable(z.string()),
        z: z.nullable(z.string()),
        css: z.nullable(z.string()),
        gui_css: z.nullable(z.string()),
        css_template: z.nullable(z.string()),
        template: z.nullable(z.string()),
    })
    .passthrough();

export type GenericWidgetConfigurationDetails = z.infer<
    typeof ZodGenericWidgetConfigurationDetails
>;

export const ZodBaseWidgetConfiguration = z
    .object({
        config: ZodGenericWidgetConfigurationDetails,
        css_templates: z.nullable(z.string()),
        custom_css_class: z.nullable(z.string()),
        desaturation: z.nullable(z.number()), //todo: confirm type
        duration: z.number(),
        filler: z.union([z.literal(0), z.literal(1)]),
        locked: z.union([z.literal(0), z.literal(1)]),
        minimum_loop_time: z.nullable(z.string()), //Actually number
        order: z.number(),
        override: z.union([z.literal(0), z.literal(1)]),
        priority: z.number(), //todo: confirm type
        repellent: z.union([z.literal(0), z.literal(1)]),
        saturation: z.number(),
        type: ZodWidgetType,
        widgetsid: z.number(),
        widgetsname: z.string(),
    })
    .passthrough();

export type BaseWidgetConfiguration = z.infer<typeof ZodBaseWidgetConfiguration>;

export type WidgetWrapperElementConfiguration = {
    widgetId: number;
    widgetName: string;
    widgetType: WidgetType;
    customCssClass: string | null;
    widgetWidth: string | null;
    widgetHeight: string | null;
    x?: string | null;
    y?: string | null;
    z?: string | null;
    widgetCss: string | null;
    guiCss: string | null;
};

/**
 * How to create TimelineSlot subtypes
 *
 * @example
 * ```typescript
 * //First create a type for the slot parameters
 * type MyWidgetTimelineSlotParams = {
 *     a: number,
 *     b: boolean,
 *     c: string,
 * }
 *
 * //Next, create a type from TimelineSlot using your param type and Widget class
 * type MyWidgetTimelineSlot = TimelineSlot<MyWidgetTimelineSlotParams, MyWidget>;
 *
 * //Now you can reuse this type for TimelineSlot functions within the widget class
 * querySlotsLinear(): MyWidgetTimelineSlot[] {
 *     //NOTICE -- The generics for generateSlot are inferred
 *     const mySlot = this.generateSlot( 1000, undefined, {a: 1, b: false, c: "c"});
 *     return [ mySlot ];
 * }
 * ```
 */
export type TimelineSlot<TParams = unknown, TWidget extends Widget = Widget> = {
    widget: TWidget;
    duration: number;
    params: TParams;
    /**
     * Slots that share a groupIdentifier will be played back to back, in an
     * order based on their presented index during the interleave function. Use
     * {@link Widget.generateGroupIdentifier} to generate a
     * standardized groupIdentifier
     */
    groupIdentifier: string | null;
};

//TODO: Refactor Fade Command uses to only accept full interfaces rather
// than Partial<Fade(In/Out)Command>
export interface FadeInCommand {
    /**
     * A boolean describing if the widget would like the zone to abort
     * its current loop
     */
    abortLoop: boolean;
}

export interface FadeOutCommand {
    /**
     * A boolean describing if the widget should fade in/out
     * Useful if the next widget is the same type
     */
    cancelFade: boolean;

    /**
     * A boolean describing if zone triggers and interrupts should be used
     */
    suppressTriggers: boolean;
}
